'use memo';

import { type FocusEvent, useCallback, useEffect, useState } from 'react';
import { AgxColumn, AgxCommissionPlan, AgxNotificationBox } from '@urbanx/agx.ui-components2';
import { CommissionDetails } from '@urbanx/agx.campaigns.client';
import { TaskStepDto } from '@urbanx/agx.activities.client';
import { hasBelowMinimumCommission } from './commissionMinimumAlert';

export const AgreementCommissionStageSteps = ({
  commissionDetails,
  onCommissionUpdated,
  allowedCommissionTypes,
  currentCommissionInfo,
  commissionStep,
  updateCommissionTaskStep,
  registerTaskModalPageValidation,
}: {
  commissionDetails?: CommissionDetails;
  onCommissionUpdated: (updated: { value: CommissionDetails }) => void;
  allowedCommissionTypes: any[];
  currentCommissionInfo?: CommissionDetails;
  commissionStep: TaskStepDto;
  updateCommissionTaskStep: (isCompleted: boolean) => void;
  registerTaskModalPageValidation?: (
    handler: (() => boolean | Promise<boolean>) | undefined
  ) => void;
}) => {
  const selectedCommissionDetails = commissionDetails ?? currentCommissionInfo;
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showLowCommissionWarning, setShowLowCommissionWarning] = useState(false);
  const [commissionIsValid, setCommissionIsValid] = useState(false);

  const handleCommissionValidChange = useCallback(
    (valid: boolean) => {
      setCommissionIsValid(valid);

      if ((commissionStep.isCompleted ?? false) !== valid) {
        updateCommissionTaskStep(valid);
      }
    },
    [commissionStep.isCompleted, updateCommissionTaskStep]
  );

  useEffect(() => {
    if (!registerTaskModalPageValidation) return;

    registerTaskModalPageValidation(() => {
      if (commissionIsValid) return true;

      setShowValidationErrors(true);
      return false;
    });

    return () => registerTaskModalPageValidation(undefined);
  }, [commissionIsValid, registerTaskModalPageValidation]);

  const handleCommissionTypeChange = () => {
    setCommissionIsValid(false);
    updateCommissionTaskStep(false);
    setShowValidationErrors(false);
    setShowLowCommissionWarning(false);
  };

  const handleCommissionFieldBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.inputMode !== 'decimal' && event.target.inputMode !== 'numeric') return;

    setShowLowCommissionWarning(true);
  };

  return (
    <AgxColumn gap="spacing-xl" fill>
      <div
        style={{ width: '100%' }}
        onFocusCapture={() => setShowLowCommissionWarning(false)}
        onBlurCapture={handleCommissionFieldBlur}
      >
        <AgxCommissionPlan
          id="commissionPlan"
          commissionTypes={allowedCommissionTypes}
          onValueChanged={onCommissionUpdated}
          formName="Agency Agreement"
          defaultValue={selectedCommissionDetails}
          required
          validateCommissionTypeSelection
          commissionTypeSelectionErrorText="Please select a commission option"
          showCommissionRangeDisclaimer
          onValidateStatusChange={handleCommissionValidChange}
          showValidationErrors={showValidationErrors}
          onCommissionTypeChange={handleCommissionTypeChange}
          useCommissionErrorLabel
        />
      </div>

      {showValidationErrors && !commissionIsValid && (
        <AgxNotificationBox
          type="danger"
          heading=""
          body="Complete all commission details before proceeding."
        />
      )}

      {showLowCommissionWarning &&
        commissionIsValid &&
        hasBelowMinimumCommission(selectedCommissionDetails) && (
          <AgxNotificationBox
            type="warning"
            heading=""
            body="Commission rate is low. Alert Admin Manager and confirm rate is correct before proceeding."
          />
        )}
    </AgxColumn>
  );
};

export default AgreementCommissionStageSteps;
